import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  Card,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import {
  useGetStaffAvailabilityQuery,
  useCreateStaffAvailabilityMutation,
  useDeleteStaffAvailabilityMutation,
  useGetAvailableStaffQuery,
  useGetTeamQuery,
} from "../../../Api/attoDeskApi";
import { ITeam } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import SearchIcon from "@mui/icons-material/Search";

interface IAvailabilitySlot {
  id: string;
  staffId: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

interface IAvailableStaff {
  id: string;
  teamFirstName: string;
  middle_name: string;
  teamLastName: string;
  teamEmail: string;
}

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00",
  "18:00", "19:00", "20:00",
];

const capitalizeFirstLetter = (name: string) =>
  name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : "";

const Index = () => {
  const classes = useStyles();
  const { showErrorMessage, showMessage } = useNotifier();

  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [newSlotDay, setNewSlotDay] = useState("");
  const [newSlotStart, setNewSlotStart] = useState("");
  const [newSlotEnd, setNewSlotEnd] = useState("");

  // Find available staff filters
  const [searchDay, setSearchDay] = useState("");
  const [searchTime, setSearchTime] = useState("");
  const [searchTriggered, setSearchTriggered] = useState(false);

  const { data: teamData, isLoading: teamLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const staffList: ITeam[] = useMemo(() => {
    return Array.isArray(teamData?.data) ? (teamData?.data as ITeam[]) : [];
  }, [teamData?.data]);

  const { data: availabilityData, isLoading: availLoading, refetch: refetchAvailability } =
    useGetStaffAvailabilityQuery(
      { staffId: selectedStaffId },
      { skip: !selectedStaffId }
    );

  const slots: IAvailabilitySlot[] = useMemo(() => {
    return Array.isArray(availabilityData?.data)
      ? (availabilityData?.data as IAvailabilitySlot[])
      : [];
  }, [availabilityData?.data]);

  const { data: availableStaffData, isLoading: availStaffLoading } =
    useGetAvailableStaffQuery(
      { day: searchDay, time: searchTime },
      { skip: !searchTriggered || !searchDay || !searchTime }
    );

  const availableStaff: IAvailableStaff[] = useMemo(() => {
    return Array.isArray(availableStaffData?.data)
      ? (availableStaffData?.data as IAvailableStaff[])
      : [];
  }, [availableStaffData?.data]);

  const [createSlot] = useCreateStaffAvailabilityMutation();
  const [deleteSlot] = useDeleteStaffAvailabilityMutation();

  // Build weekly grid data
  const weeklyGrid = useMemo(() => {
    const grid: Record<string, IAvailabilitySlot[]> = {};
    DAYS_OF_WEEK.forEach((day) => {
      grid[day] = slots.filter((s) => s.dayOfWeek === day);
    });
    return grid;
  }, [slots]);

  const handleAddSlot = async () => {
    if (!selectedStaffId || !newSlotDay || !newSlotStart || !newSlotEnd) {
      showErrorMessage("Please select staff, day, start time and end time");
      return;
    }
    try {
      const response = await createSlot({
        staffId: selectedStaffId,
        dayOfWeek: newSlotDay,
        startTime: newSlotStart,
        endTime: newSlotEnd,
      }).unwrap();
      if (response.status) {
        showMessage("Availability slot added successfully");
        setNewSlotDay("");
        setNewSlotStart("");
        setNewSlotEnd("");
        refetchAvailability();
      } else {
        showErrorMessage("Failed to add availability slot");
      }
    } catch (error) {
      showErrorMessage("Failed to add availability slot");
    }
  };

  const handleDeleteSlot = async (id: string) => {
    try {
      const response = await deleteSlot(id).unwrap();
      if (response.status) {
        showMessage("Slot deleted successfully");
        refetchAvailability();
      } else {
        showErrorMessage("Failed to delete slot");
      }
    } catch (error) {
      showErrorMessage("Failed to delete slot");
    }
  };

  const handleSearchAvailableStaff = () => {
    if (!searchDay || !searchTime) {
      showErrorMessage("Please select a day and time to search");
      return;
    }
    setSearchTriggered(true);
  };

  const pageIsLoading = teamLoading;

  if (pageIsLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
    );

  return (
    <>
      {/* Staff Availability Management */}
      <Card className={classes.card} sx={{ mb: 2 }}>
        <Grid
          container
          justifyContent={"space-between"}
          spacing={2}
          sx={{ padding: 2 }}
          className={classes.titleGrid}
        >
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Staff Availability
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Select Staff"
                  value={selectedStaffId}
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                  className={classes.searchTextField}
                >
                  <MenuItem value="">-- Select Staff --</MenuItem>
                  {staffList.map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {capitalizeFirstLetter(staff.teamFirstName || "") +
                        " " +
                        capitalizeFirstLetter(staff.teamLastName || "")}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {selectedStaffId && (
          <>
            {/* Add new slot */}
            <Grid container spacing={1} sx={{ padding: 2 }} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Day"
                  value={newSlotDay}
                  onChange={(e) => setNewSlotDay(e.target.value)}
                  className={classes.searchTextField}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Start Time"
                  value={newSlotStart}
                  onChange={(e) => setNewSlotStart(e.target.value)}
                  className={classes.searchTextField}
                >
                  {TIME_SLOTS.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="End Time"
                  value={newSlotEnd}
                  onChange={(e) => setNewSlotEnd(e.target.value)}
                  className={classes.searchTextField}
                >
                  {TIME_SLOTS.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item>
                <Button
                  className={classes.addNewButton}
                  startIcon={<AddIcon />}
                  onClick={handleAddSlot}
                >
                  Add Slot
                </Button>
              </Grid>
            </Grid>

            {/* Weekly Grid */}
            {availLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid sx={{ padding: 2 }}>
                <TableContainer>
                  <Table className={classes.table}>
                    <TableHead className={classes.tableHeaderRow}>
                      <TableRow>
                        <TableCell className={classes.tablheader} sx={{ minWidth: 120 }}>
                          <strong>Day</strong>
                        </TableCell>
                        <TableCell className={classes.tablheader}>
                          <strong>Available Time Slots</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {DAYS_OF_WEEK.map((day) => (
                        <TableRow key={day} className={classes.tableRowHover}>
                          <TableCell className={classes.tablCell} sx={{ fontWeight: 600 }}>
                            {day}
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            {weeklyGrid[day]?.length > 0 ? (
                              <Grid container spacing={1}>
                                {weeklyGrid[day].map((slot) => (
                                  <Grid item key={slot.id}>
                                    <Chip
                                      label={`${slot.startTime} - ${slot.endTime}`}
                                      size="small"
                                      onDelete={() => handleDeleteSlot(slot.id)}
                                      className={classes.greeenthemechip}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No availability
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </>
        )}
      </Card>

      {/* Find Available Staff */}
      <Card className={classes.card}>
        <Grid
          container
          justifyContent={"space-between"}
          spacing={2}
          sx={{ padding: 2 }}
          className={classes.titleGrid}
        >
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Find Available Staff
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Day"
                  value={searchDay}
                  onChange={(e) => {
                    setSearchDay(e.target.value);
                    setSearchTriggered(false);
                  }}
                  className={classes.searchTextField}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Time"
                  value={searchTime}
                  onChange={(e) => {
                    setSearchTime(e.target.value);
                    setSearchTriggered(false);
                  }}
                  className={classes.searchTextField}
                >
                  {TIME_SLOTS.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item>
                <Button
                  className={classes.addNewButton}
                  startIcon={<SearchIcon />}
                  onClick={handleSearchAvailableStaff}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {searchTriggered && (
          <Grid sx={{ padding: 2 }}>
            {availStaffLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead className={classes.tableHeaderRow}>
                    <TableRow>
                      <TableCell className={classes.tablheaderId}>
                        <strong>#</strong>
                      </TableCell>
                      <TableCell className={classes.tablheader}>
                        <strong>Staff Name</strong>
                      </TableCell>
                      <TableCell className={classes.tablheader}>
                        <strong>Email</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableStaff.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No staff available for the selected day and time
                        </TableCell>
                      </TableRow>
                    ) : (
                      availableStaff.map((staff, index) => (
                        <TableRow key={staff.id} className={classes.tableRowHover}>
                          <TableCell scope="row" className={classes.tablCellId}>
                            {index + 1}
                          </TableCell>
                          <TableCell scope="row" className={classes.tablCell}>
                            {capitalizeFirstLetter(staff.teamFirstName || "") +
                              " " +
                              capitalizeFirstLetter(staff.middle_name || "") +
                              " " +
                              capitalizeFirstLetter(staff.teamLastName || "")}
                          </TableCell>
                          <TableCell scope="row" className={classes.tablCell}>
                            {staff.teamEmail || ""}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        )}
      </Card>
    </>
  );
};

export default Index;
